import React from 'react';

const VendorCard = ({ 
  vendor, 
  index, 
  category, 
  onVendorClick, 
  showCompatibility = false,
  layoutMode = 'horizontal' // 'horizontal' | 'grid'
}) => {
  const categories = [
    { name: 'Venue', icon: '🏛️', color: 'from-rose-100 to-pink-200' },
    { name: 'Catering', icon: '🍽️', color: 'from-champagne-100 to-gold-200' },
    { name: 'Photography', icon: '📸', color: 'from-purple-100 to-lavender-200' },
    { name: 'Flowers', icon: '🌸', color: 'from-pink-100 to-rose-200' },
    { name: 'Music', icon: '🎵', color: 'from-blue-100 to-indigo-200' },
    { name: 'Attire', icon: '👗', color: 'from-mauve-100 to-purple-200' },
    { name: 'Transportation', icon: '🚗', color: 'from-teal-100 to-cyan-200' },
    { name: 'Planning', icon: '📋', color: 'from-blush-100 to-pink-200' }
  ];

  const getCategoryData = (categoryName) => {
    return categories.find(c => c.name === categoryName) || categories[0];
  };

  const getCompatibilityColor = (score) => {
    if (score >= 80) return 'text-green-600 bg-green-100';
    if (score >= 65) return 'text-blue-600 bg-blue-100';
    if (score >= 50) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getCompatibilityLabel = (score) => {
    if (score >= 80) return 'Excellent Match';
    if (score >= 65) return 'Great Match';
    if (score >= 50) return 'Good Match';
    return 'Moderate Match';
  };

  return (
    <div
      className={`group flex-shrink-0 relative ${
        layoutMode === 'grid' ? 'w-full' : 'w-80'
      }`}
      style={{
        animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
      }}
    >
      {/* Magical Vendor Card - Fixed Height */}
      <div
        className="relative bg-gradient-to-br from-white via-rose-50/30 to-pink-50/30 rounded-2xl border border-rose-200/50 shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:scale-105 hover:-translate-y-2 cursor-pointer overflow-hidden h-80"
        onClick={() => onVendorClick(vendor)}
      >
        {/* Magical Shimmer Effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
        
        {/* Floating Sparkles */}
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="flex gap-1">
            <div className="w-1 h-1 bg-rose-400 rounded-full animate-ping" style={{animationDelay: '0s'}}></div>
            <div className="w-1 h-1 bg-pink-400 rounded-full animate-ping" style={{animationDelay: '0.2s'}}></div>
            <div className="w-1 h-1 bg-champagne-400 rounded-full animate-ping" style={{animationDelay: '0.4s'}}></div>
          </div>
        </div>

        <div className="p-6 h-full flex flex-col">
          {/* Header with Category Icon and Optional Compatibility Badge */}
          <div className="flex justify-between items-start mb-4">
            {showCompatibility && (
              <div className="relative">
                <div className={`px-3 py-1 rounded-full text-sm font-bold shadow-lg ${getCompatibilityColor(vendor.compatibility_score)} border-2 border-white/50`}>
                  <span className="text-xs opacity-75">#{index + 1}</span>
                  <span className="mx-2">•</span>
                  <span>{vendor.compatibility_score}%</span>
                  <span className="ml-2 text-xs">{getCompatibilityLabel(vendor.compatibility_score)}</span>
                </div>
                {/* Magical Glow */}
                <div className={`absolute inset-0 rounded-full blur-md opacity-30 ${getCompatibilityColor(vendor.compatibility_score)}`}></div>
              </div>
            )}
            {!showCompatibility && <div></div>}
            <div className="text-3xl transform group-hover:scale-110 group-hover:animate-bounce transition-transform duration-300">
              {getCategoryData(category)?.icon}
            </div>
          </div>

          {/* Vendor Info with VowAI Theme Card Effects - Dynamic Layout */}
          <div className="flex-grow flex flex-col">
            <h4 className="text-lg font-bold mb-3 leading-tight group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-rose-600 group-hover:via-pink-600 group-hover:to-purple-600 transition-all duration-300 text-gray-800">
              {vendor.name}
            </h4>
            <p className="text-gray-600 mb-4 leading-relaxed text-sm flex-grow">{vendor.tagline}</p>
          </div>
          
          {/* Rating - Fixed at Bottom */}
          <div className="mb-4 p-3 bg-gradient-to-r from-rose-50 to-pink-50 rounded-xl border border-rose-100">
            <div className="flex items-center gap-2 mb-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className={`text-sm ${i < Math.floor(vendor.rating) ? 'text-yellow-400' : 'text-gray-200'}`}>
                    ⭐
                  </span>
                ))}
              </div>
              <span className="font-bold text-gray-700 text-sm">{vendor.rating}</span>
              <span className="text-gray-500 text-xs">({vendor.reviews} reviews)</span>
            </div>
            {/* Location on separate line */}
            <div className="text-sm text-rose-600 font-medium">
              📍 {vendor.location}
            </div>
          </div>

          {/* Magical View Profile Button - Fixed at Bottom */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onVendorClick(vendor);
            }}
            className="group relative w-full bg-gradient-to-r from-rose-500 via-pink-500 to-rose-600 hover:from-rose-600 hover:via-pink-600 hover:to-rose-700 text-white py-3 px-6 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-xl active:scale-95 overflow-hidden mt-auto"
          >
            <span className="relative z-10">View Profile</span>
            {/* Button Shimmer */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
          </button>
        </div>

        {/* Magical Border Glow */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-rose-200 via-pink-200 to-rose-200 opacity-0 group-hover:opacity-50 transition-opacity duration-300 -z-10 blur-sm"></div>
      </div>

      {/* Animation Styles */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default VendorCard;
